/**
 * Hook orquestrador do dashboard configuravel (Bloco VG).
 *
 * Responsabilidades:
 *   1. Resolver lista de widgets ativos = preferencia ∩ permissoes ∩ catalogo
 *   2. Fetch paralelo (Promise.allSettled) dos dados de cada widget
 *   3. Polling 60s; pausa quando document.hidden=true
 *   4. Erro/loading isolados por widget
 *
 * Uso:
 *   const { widgetsAtivos, dados, loading, error, recarregar }
 *     = useDashboardWidgets({ preferencia, request, isMaster, hasAllPermissions });
 *
 * `preferencia` = lista [{id, posicao?, tamanho?}, ...] do backend.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { WIDGETS_POR_ID, filtrarPorPermissoes } from "./_catalogo";


const INTERVALO_POLLING_MS = 60_000;


/**
 * Resolve widgets ativos = preferencia ∩ permissoes ∩ catalogo.
 * Mantem a ordem da preferencia. Ignora ids que nao existem mais no
 * catalogo (compat futuro). Filtra por permissao.
 */
function resolverAtivos(preferencia, permitidosIds) {
  const permitSet = new Set(permitidosIds);
  return (preferencia || [])
    .filter((p) => p && p.id && WIDGETS_POR_ID[p.id] && permitSet.has(p.id))
    .map((p, idx) => ({
      id: p.id,
      posicao: typeof p.posicao === "number" ? p.posicao : idx,
      tamanho: p.tamanho || WIDGETS_POR_ID[p.id].tamanho_default || "M",
      def: WIDGETS_POR_ID[p.id],
    }))
    .sort((a, b) => a.posicao - b.posicao);
}


export function useDashboardWidgets({
  preferencia,
  request,
  isMaster,
  hasAllPermissions,
}) {
  const permitidos = useMemo(
    () => filtrarPorPermissoes(hasAllPermissions, isMaster).map((w) => w.id),
    [hasAllPermissions, isMaster],
  );

  const widgetsAtivos = useMemo(
    () => resolverAtivos(preferencia, permitidos),
    [preferencia, permitidos],
  );

  const [dados, setDados] = useState({});      // { [id]: payload }
  const [loading, setLoading] = useState({});  // { [id]: true }
  const [error, setError] = useState({});      // { [id]: msg }

  const ativosRef = useRef(widgetsAtivos);
  useEffect(() => { ativosRef.current = widgetsAtivos; }, [widgetsAtivos]);

  const requestRef = useRef(request);
  useEffect(() => { requestRef.current = request; }, [request]);

  const recarregar = useCallback(async (somenteIds = null) => {
    const ativos = ativosRef.current;
    if (!ativos || ativos.length === 0) return;
    const alvo = somenteIds
      ? ativos.filter((w) => somenteIds.includes(w.id))
      : ativos;

    // Marca loading dos alvos
    setLoading((prev) => {
      const next = { ...prev };
      alvo.forEach((w) => { next[w.id] = true; });
      return next;
    });

    const results = await Promise.allSettled(
      alvo.map(async (w) => {
        if (typeof w.def.fetchData !== "function") {
          return { id: w.id, dados: null, error: null };
        }
        try {
          const out = await w.def.fetchData(requestRef.current);
          return { id: w.id, dados: out, error: null };
        } catch (err) {
          return {
            id: w.id,
            dados: null,
            error: err?.message || "Erro ao carregar widget.",
          };
        }
      }),
    );

    setDados((prev) => {
      const next = { ...prev };
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          next[r.value.id] = r.value.dados;
        }
      });
      return next;
    });
    setError((prev) => {
      const next = { ...prev };
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          if (r.value.error) next[r.value.id] = r.value.error;
          else delete next[r.value.id];
        } else {
          // Promise rejeitada — fallback (raro pois cada fetch tem try/catch)
          // Nao temos id confiavel aqui; ignora.
        }
      });
      return next;
    });
    setLoading((prev) => {
      const next = { ...prev };
      alvo.forEach((w) => { next[w.id] = false; });
      return next;
    });
  }, []);

  // Mount: carrega tudo
  useEffect(() => {
    recarregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetsAtivos.map((w) => w.id).join(",")]);

  // Polling 60s — pausa em background
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      recarregar();
    }, INTERVALO_POLLING_MS);
    return () => clearInterval(id);
  }, [recarregar]);

  return { widgetsAtivos, dados, loading, error, recarregar };
}
